import React, { useEffect, Suspense, useState } from 'react'
import ReactDOM from 'react-dom'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTabNavigation } from 'hooks/useTabNavigation'
import { TabMenuBar } from './TabMenuBar'
import { TabContentRenderer } from './TabContentRenderer'
import { useLateralMenu } from 'hooks/useLateralMenu'
import ContentOverlayLoader from 'components/ContentOverlayLoader'
import currency_parametersActions from 'store/actions/currency_parameters'

/**
 * UnifiedPrivateLayout
 *
 * A single, persistent layout shell used by all private routes.
 * It stays mounted even when navigating between different routes in the Switch,
 * allowing the TabContentRenderer to keep tab components alive in the DOM.
 *
 * The Header and Sidebar always reflect the CURRENT active view's layout config
 * (updated by ViewRegistrar on every URL change), while tab content is frozen
 * per-tab so it never remounts or refetches.
 */
export const UnifiedPrivateLayout = () => {
    const history = useHistory()
    const location = useLocation()
    const { tabs, activeTabId, activeView, syncTabWithLocation, tabCache } = useTabNavigation()
    const { expand } = useLateralMenu()
    const dispatch = useDispatch()
    // Auth check — hide the entire layout shell when not authenticated
    const isAuthenticated = useSelector(state =>
        !!state.app.user || !!state.app.client || !!state.app.driver ||
        !!state.app.passenger || !!state.app.supplier || !!state.app.superAdmin
    )

    // Currency parameters live in the admin (User) portal only; the /currency_parameter
    // route authenticates User tokens, so loading it for other sessions (superadmin,
    // client, driver, passenger, supplier) would 401.
    const isUserAuthenticated = useSelector(state => !!state.app.user)

    useEffect(() => {
        if (isUserAuthenticated) dispatch(currency_parametersActions.load({ force: true }))
    }, [isUserAuthenticated])
    const isPublicPage = /^\/(login|landing|public_forms)/.test(location.pathname)
    const shouldHide = !isAuthenticated || isPublicPage

    const { Component, routeProps, layoutConfig } = activeView

    // Resolve layout config — when switching to an existing tab, we want the
    // header/sidebar to match that tab's original layout, not the "current URL"
    // layout (which may still be updating via ViewRegistrar's useEffect).
    // We fall back to the active tab's cached layoutConfig for instant accuracy.
    const resolvedLayoutConfig = (() => {
        if (activeTabId && tabCache[activeTabId]?.layoutConfig) {
            return tabCache[activeTabId].layoutConfig
        }
        return layoutConfig
    })()

    const {
        Header: LayoutHeader,
        Sidebar: LayoutSidebar,
        sidebarProps = {},
        sidebarWidth = 225,
        layoutProps = {}
    } = resolvedLayoutConfig || {}
    const isShellReady = !!resolvedLayoutConfig
    const [isInitialContentSettling, setIsInitialContentSettling] = useState(true)

    // Navbar (60px). Tab bar is rendered inside content flow.
    const mainPaddingTop = '60px'
    const resolvedSidebarWidth = `${sidebarWidth}px`

    useEffect(() => {
        if (shouldHide) return
        if (!routeProps?.location?.pathname) return
        syncTabWithLocation(routeProps.location.pathname)
    }, [routeProps?.location?.pathname, syncTabWithLocation, shouldHide])

    // On page refresh, tabs state is lost. If we are on a tab path but tabs are empty,
    // redirect to the initial route "/" as requested.
    useEffect(() => {
        if (shouldHide) return
        if (tabs.length === 0 && history.location.pathname !== '/' && history.location.pathname !== '/login') {
            history.replace('/');
        }
    }, [tabs.length, history, shouldHide]);

    // Keep the first content loader a bit longer on hard refresh
    // to avoid the "chunked pop-in" effect while initial sections mount.
    useEffect(() => {
        if (shouldHide) return
        if (!isInitialContentSettling) return
        if (!Component) return

        let timeoutId = null
        const rafId = window.requestAnimationFrame(() => {
            timeoutId = window.setTimeout(() => {
                setIsInitialContentSettling(false)
            }, 280)
        })

        return () => {
            window.cancelAnimationFrame(rafId)
            if (timeoutId) window.clearTimeout(timeoutId)
        }
    }, [Component, isInitialContentSettling, shouldHide])

    // --- Early return AFTER all hooks ---
    if (shouldHide) return null

    const effectiveHistory = routeProps?.history || history
    const showContentLoader = !Component || isInitialContentSettling
    console.log('[UnifiedPrivateLayout] DIAG', {
        hasComponent: !!Component,
        isInitialContentSettling,
        showContentLoader,
        activeTabId,
        tabsLength: tabs.length,
        pathname: routeProps?.location?.pathname,
    })
    const contentLoader = <ContentOverlayLoader active={true} text='Cargando...' offsetY={20} />

    return (
        <div className="unified-layout-root">
            {/* Header — solo monta cuando el layout está resuelto */}
            {isShellReady && LayoutHeader && (
                <LayoutHeader
                    {...layoutProps}
                    {...sidebarProps}
                    history={effectiveHistory}
                    expand={expand}
                />
            )}

            <div
                className='main-content'
                style={
                    expand == null || expand == 1
                        ? { marginLeft: resolvedSidebarWidth, paddingTop: mainPaddingTop }
                        : { paddingTop: mainPaddingTop }
                }
            >
                {/* Portal sidebar to root element (legacy pattern of the app) */}
                {isShellReady && LayoutSidebar && ReactDOM.createPortal(
                    <LayoutSidebar
                        {...sidebarProps}
                        history={effectiveHistory}
                        location={routeProps?.location}
                        expand={expand}
                    />,
                    document.getElementById('root')
                )}

                {/* Tab strip — espera al mismo gate que Header y Sidebar */}
                {isShellReady && <TabMenuBar history={effectiveHistory} />}

                {/* The actual page content area (supporting tabs) */}
                {showContentLoader ? (
                    contentLoader
                ) : (
                    <Suspense fallback={contentLoader}>
                        <TabContentRenderer
                            tabs={tabs}
                            activeTabId={activeTabId}
                            Component={Component}
                            routeProps={routeProps}
                            layoutConfig={layoutConfig}
                        />
                    </Suspense>
                )}
            </div>
        </div>
    )
}
